/* ============================================================
 * Maison shared interactions
 * Enhances Maison sections (homepage + Maison PDP templates) with:
 *   1. Button hover / active animation  (.maison-anim-btn)
 *   2. Skeleton image loader            (img.maison-img)
 *   3. Stats count-up                   (.maison-stats-hero1-section .msh__stat-value)
 * The "second image on hover" swap is pure CSS (see maison-interactions.css);
 * this file only enhances behaviour that needs scripting.
 * ============================================================ */
(function () {
  'use strict';

  var MAISON_SECTION = '[id^="shopify-section-"][class*="maison-"]';

  /* -------------------------------------------------------- */
  /* 1. Buttons: wrap the label so it can animate on hover.   */
  /* -------------------------------------------------------- */
  function enhanceButton(btn) {
    if (btn.dataset.maisonBtnReady) return;
    btn.dataset.maisonBtnReady = '1';

    var labels = btn.querySelectorAll('[data-maison-anim-label]');
    if (!labels.length) {
      var label = document.createElement('span');
      label.className = 'maison-anim-btn__label';
      while (btn.firstChild) label.appendChild(btn.firstChild);
      btn.appendChild(label);
      labels = [label];
    }

    Array.prototype.forEach.call(labels, function (label) {
      if (label.dataset.maisonAnimReady) return;
      label.dataset.maisonAnimReady = '1';

      var current = document.createElement('span');
      current.className = 'maison-anim-btn__inner maison-anim-btn__inner--current';
      while (label.firstChild) current.appendChild(label.firstChild);

      var incoming = current.cloneNode(true);
      incoming.className = 'maison-anim-btn__inner maison-anim-btn__inner--incoming';
      incoming.setAttribute('aria-hidden', 'true');

      var track = document.createElement('span');
      track.className = 'maison-anim-btn__track';
      track.appendChild(current);
      track.appendChild(incoming);
      label.appendChild(track);
    });
  }

  function enhanceButtons(scope) {
    var buttons = (scope || document).querySelectorAll('.maison-anim-btn');
    Array.prototype.forEach.call(buttons, enhanceButton);
  }

  /* -------------------------------------------------------- */
  /* 2. Skeleton image loader.                                */
  /* -------------------------------------------------------- */
  function markLoaded(img) {
    img.classList.add('is-loaded');
  }

  function shouldSkip(img) {
    // Skip small icon/decorative images and the hover-swap overlays.
    if (img.classList.contains('maison-hover-img')) return true;
    var src = img.currentSrc || img.getAttribute('src') || '';
    if (/\.svg(\?|$)/i.test(src)) return true;
    var w = parseInt(img.getAttribute('width'), 10);
    var h = parseInt(img.getAttribute('height'), 10);
    if ((w && w <= 48) || (h && h <= 48)) return true;
    return false;
  }

  function enhanceImage(img) {
    if (img.dataset.maisonImgReady) return;
    if (shouldSkip(img)) return;
    img.dataset.maisonImgReady = '1';
    img.classList.add('maison-img');

    if (img.complete && img.naturalWidth > 0) {
      markLoaded(img);
      return;
    }
    img.addEventListener('load', function () { markLoaded(img); }, { once: true });
    // If the image fails, remove the skeleton so we don't shimmer forever.
    img.addEventListener('error', function () { markLoaded(img); }, { once: true });
  }

  function enhanceImages(scope) {
    var roots = scope && scope.matches && scope.matches(MAISON_SECTION)
      ? [scope]
      : (scope || document).querySelectorAll(MAISON_SECTION);
    Array.prototype.forEach.call(roots, function (root) {
      Array.prototype.forEach.call(root.querySelectorAll('img'), enhanceImage);
    });
  }

  var deferredMediaObserver = null;
  var deferredHoverReady = false;

  function hydrateDeferredImage(img) {
    if (!img || !img.dataset.maisonSrc) return;
    var picture = img.closest('picture');
    if (picture) {
      Array.prototype.forEach.call(picture.querySelectorAll('source[data-maison-srcset]'), function (source) {
        source.srcset = source.dataset.maisonSrcset;
        delete source.dataset.maisonSrcset;
      });
    }
    if (img.dataset.maisonSrcset) img.srcset = img.dataset.maisonSrcset;
    img.src = img.dataset.maisonSrc;
    delete img.dataset.maisonSrc;
    delete img.dataset.maisonSrcset;
  }

  var autoplayRetryVideos = [];
  var autoplayRetryBound = false;

  function retryAutoplayOnGesture() {
    var videos = autoplayRetryVideos.splice(0);
    videos.forEach(function (video) {
      if (!video.isConnected || !video.paused) return;
      var playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(function () {});
    });
    document.removeEventListener('touchstart', retryAutoplayOnGesture);
    document.removeEventListener('click', retryAutoplayOnGesture);
    autoplayRetryBound = false;
  }

  function queueAutoplayRetry(video) {
    if (autoplayRetryVideos.indexOf(video) === -1) autoplayRetryVideos.push(video);
    if (autoplayRetryBound) return;
    autoplayRetryBound = true;
    document.addEventListener('touchstart', retryAutoplayOnGesture, { passive: true });
    document.addEventListener('click', retryAutoplayOnGesture);
  }

  function hydrateDeferredVideo(video) {
    if (!video || !video.hasAttribute('data-maison-autoplay')) return;
    video.preload = 'metadata';
    // iOS requires the muted property (not just the attribute) for programmatic playback.
    video.muted = true;
    video.load();
    var playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(function () {
        // Autoplay refused (e.g. iOS Low Power Mode) — retry on the first user gesture.
        queueAutoplayRetry(video);
      });
    }
    video.removeAttribute('data-maison-autoplay');
  }

  function hydrateDeferredMedia(element) {
    if (element.tagName === 'VIDEO') hydrateDeferredVideo(element);
    else hydrateDeferredImage(element);
  }

  function observeDeferredMedia(scope) {
    var media = (scope || document).querySelectorAll('img[data-maison-src], video[data-maison-autoplay]');
    if (!media.length) return;

    if (!('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(media, hydrateDeferredMedia);
      return;
    }

    if (!deferredMediaObserver) {
      deferredMediaObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          hydrateDeferredMedia(entry.target);
          deferredMediaObserver.unobserve(entry.target);
        });
      }, { rootMargin: '600px' });
    }

    Array.prototype.forEach.call(media, function (element) {
      deferredMediaObserver.observe(element);
    });
  }

  function setupDeferredHoverImages() {
    if (deferredHoverReady) return;
    deferredHoverReady = true;

    function hydrateHoveredCard(event) {
      var media = event.target.closest && event.target.closest('.maison-media-swap');
      if (!media) return;
      var image = media.querySelector('.maison-hover-img[data-maison-hover-src]');
      if (!image) return;
      image.dataset.maisonSrc = image.dataset.maisonHoverSrc;
      image.dataset.maisonSrcset = image.dataset.maisonHoverSrcset || '';
      delete image.dataset.maisonHoverSrc;
      delete image.dataset.maisonHoverSrcset;
      hydrateDeferredImage(image);
    }

    document.addEventListener('pointerover', hydrateHoveredCard);
    document.addEventListener('focusin', hydrateHoveredCard);
  }

  /* -------------------------------------------------------- */
  /* 3. Stats count-up.                                       */
  /* -------------------------------------------------------- */
  var PREFERS_REDUCED_MOTION =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function parseStat(text) {
    // Split "96%" / "1,545" / "4.9" / "20+" into prefix, number, suffix.
    var match = text.match(/-?[\d.,]+/);
    if (!match) return null;
    var raw = match[0];
    var start = match.index;
    var numeric = parseFloat(raw.replace(/,/g, ''));
    if (!isFinite(numeric)) return null;
    var decimals = 0;
    var dot = raw.indexOf('.');
    if (dot !== -1) decimals = raw.length - dot - 1;
    return {
      prefix: text.slice(0, start),
      suffix: text.slice(start + raw.length),
      target: numeric,
      decimals: decimals,
      grouped: raw.indexOf(',') !== -1
    };
  }

  function formatValue(value, info) {
    var fixed = value.toFixed(info.decimals);
    if (info.grouped) {
      var parts = fixed.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      fixed = parts.join('.');
    }
    return info.prefix + fixed + info.suffix;
  }

  function countUp(el) {
    if (el.dataset.maisonCountReady) return;
    el.dataset.maisonCountReady = '1';

    var info = parseStat(el.textContent.trim());
    if (!info) return;

    if (PREFERS_REDUCED_MOTION) {
      el.textContent = formatValue(info.target, info);
      return;
    }

    var duration = 1600;
    var startTime = null;
    el.textContent = formatValue(0, info);

    function tick(now) {
      if (startTime === null) startTime = now;
      var progress = Math.min((now - startTime) / duration, 1);
      // easeOutCubic
      var eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = formatValue(info.target * eased, info);
      if (progress < 1) window.requestAnimationFrame(tick);
      else el.textContent = formatValue(info.target, info);
    }
    window.requestAnimationFrame(tick);
  }

  function observeStats(scope) {
    var values = (scope || document).querySelectorAll(
      '.maison-stats-hero1-section .msh__stat-value'
    );
    if (!values.length) return;

    if (!('IntersectionObserver' in window)) {
      Array.prototype.forEach.call(values, countUp);
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          countUp(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.4 });

    Array.prototype.forEach.call(values, function (el) {
      if (!el.dataset.maisonCountReady) observer.observe(el);
    });
  }

  /* -------------------------------------------------------- */
  /* Boot + Shopify theme editor re-init.                     */
  /* -------------------------------------------------------- */
  function init(scope) {
    enhanceButtons(scope);
    enhanceImages(scope);
    observeDeferredMedia(scope);
    setupDeferredHoverImages();
    observeStats(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    if (!deferredMediaObserver) return;
    var media = event.target.querySelectorAll('img[data-maison-src], video[data-maison-autoplay]');
    Array.prototype.forEach.call(media, function (element) {
      deferredMediaObserver.unobserve(element);
    });
  });
})();
